import { Flex } from "antd"
import { Text } from "ui/Typography"
import IconWithBg from "ui/IconWithBg"
import styles from "../index.module.css"
import moment from "moment"
import React from "react"
import PropTypes from "prop-types"
import { getEventBorderColor, getEventColor, getJournalIcon, overlaps } from "../../../utils"
import ReservationDetailModal from "pagesV2/pro/Bookings/ReservationDetailPage/ReservationDetailModal"
import EventBlock from "../EventBlock"
import Journal from "../../../Modals/Journal"
import { computeEventColumns, getEventPosition, HOUR_HEIGHT, DAY_ROW_SLOT_HEIGHT } from "../timeGridUtils"
import useDayGridData, { getResourceLabel, getResourceEvents } from "./useDayGridData"
import { DSIcon } from "ui/Icon/DSIcon"
import TimeRangeSettings from "pagesV2/pro/Calendar/Header/TimeRangeSettings"
import { useDevice } from "hooks/useDevice"
import { useTranslation } from "react-i18next"
import { DEFAULT_START_HOUR, DEFAULT_END_HOUR } from "pagesV2/pro/Calendar/constants"
import { useNavigate } from "react-router-dom"
import { buildReservationsLink } from "helpers/blackouts"
import { DATE_FORMAT } from "modules/datepicker/constants"
import BlackoutBanner from "pagesV2/pro/Calendar/shared/BlackoutBanner"
import MultiDayStrip from "./MultiDayStrip"
import { useCalendarBlackouts } from "pagesV2/pro/Calendar/hooks/useCalendarBlackouts"
import { marksBookingConflicts } from "pagesV2/pro/Calendar/blackoutUtils"

const DayGrid = ({
  date,
  hours,
  events,
  resources: resourcesProp,
  profileIds,
  boatIds,
  activeTab,
  startHour,
  endHour,
  showTimeRangeSettings,
  onStartHourChange,
  onEndHourChange,
  onResetHours,
  onOpenDayPanel
}) => {
  const { isMobile, isTablet } = useDevice()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { getDay: getDayBlackout, getBookingConflict } = useCalendarBlackouts()
  const currentDayKey = moment(date).format(DATE_FORMAT)
  const dayBlackout = getDayBlackout(currentDayKey)
  const marksConflicts = marksBookingConflicts(dayBlackout)

  const goToReservations = () =>
    navigate(buildReservationsLink({ dateFrom: currentDayKey, listingIds: dayBlackout?.experience_ids || [] }))

  const blackoutBanner = dayBlackout ? (
    <BlackoutBanner
      dayBlackout={dayBlackout}
      onViewDetails={() => onOpenDayPanel?.(currentDayKey)}
      onManageReservations={goToReservations}
    />
  ) : null
  const {
    isJournalModalOpen,
    setIsJournalModalOpen,
    isBookingModalOpen,
    setIsBookingModalOpen,
    selectedBookingId,
    setSelectedBookingId,
    currentDay,
    hasJournalPermission,
    journalNoteDates,
    active,
    timeOffForDay,
    dayEvents,
    resources,
    totalHeight,
    gridTemplateColumns,
    gridMinWidth,
    isCrewTab,
    isEquipmentTab,
    handleBookingCancelSuccess,
  } = useDayGridData({ date, hours, events, resources: resourcesProp, profileIds, boatIds, activeTab, startHour, endHour })

  const currentHour = moment().hour()
  const showCurrentTimeLine = moment(date).isSame(moment(), "day") && currentHour >= startHour && currentHour <= endHour
  const currentTimeOffset = showCurrentTimeLine
    ? Math.max(0, ((currentHour - startHour) * 60 + moment().minute()) * (HOUR_HEIGHT / 60))
    : null
  const currentTimePercent = showCurrentTimeLine
    ? Math.max(0, (((currentHour - startHour) * 60 + moment().minute()) / (hours.length * 60)) * 100)
    : null
  // ── Transposed layout: time on top, resources on left (crew / equipment) ──
  if (isCrewTab || isEquipmentTab) {
    const totalMins = hours.length * 60
    const MIN_ROWS = 4
    const resourceRows =
      resources.length === 0
        ? []
        : resources.map((r) => ({
            key: typeof r === "string" ? r : (r?.id ?? getResourceLabel(r)),
            label: getResourceLabel(r),
            resource: r
          }))
    const dummyCount = Math.max(0, MIN_ROWS - resourceRows.length)
    const rows = [...resourceRows, ...Array.from({ length: dummyCount }, (_, i) => ({ key: `dummy-${i}`, label: "", isEmpty: true }))]

    return (
      <>
        <Flex vertical className="border-t overflow-auto" flex={1}>
          <Flex justify="space-between" align="center">
            <Flex gap={8} align="center" className="py-s px-s">
              <Text>{t("calendar.journal_label", { defaultValue: "Journal" })}</Text>
              {hasJournalPermission && (
                <IconWithBg
                  icon={getJournalIcon(journalNoteDates, currentDay)}
                  onClick={active ? () => setIsJournalModalOpen(true) : undefined}
                  disabled={!active}
                />
              )}
            </Flex>
            {showTimeRangeSettings && (isMobile || isTablet) && (
              <TimeRangeSettings
                startHour={startHour}
                endHour={endHour}
                onStartChange={onStartHourChange}
                onEndChange={onEndHourChange}
                onReset={onResetHours}
                isModified={startHour !== DEFAULT_START_HOUR || endHour !== DEFAULT_END_HOUR}
              />
            )}
          </Flex>
          {blackoutBanner}
          <div className={styles.transposedGridWrapper}>
            <table className={styles.transposedTable}>
              <colgroup>
                <col style={{ width: 140, minWidth: 140 }} />
                {hours.map((h) => (
                  <col key={h} style={{ minWidth: 60 }} />
                ))}
              </colgroup>
              <thead>
                <tr>
                  <th className={styles.transposedCorner}>
                    <DSIcon name={isEquipmentTab ? "boat" : "user"} width={20} height={20} fill="var(--color-text-secondary-dark)" />
                  </th>
                  {hours.map((hour) => (
                    <th key={hour} className={styles.transposedHourCell}>
                      <Text fw="500">{moment(hour, "H").format("h A")}</Text>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, rowIdx) => {
                  const resEvents = row.isEmpty
                    ? []
                    : getResourceEvents(
                        dayEvents,
                        row.resource,
                        isEquipmentTab,
                        isCrewTab,
                        typeof row.resource === "string" ? null : row.resource?.id
                      )
                  const eventsWithCols = computeEventColumns(resEvents.filter((ev) => ev.startMoment?.isValid() && ev.endMoment?.isValid()))
                  const totalCols = eventsWithCols.length > 0 ? Math.max(...eventsWithCols.map((ev) => ev.totalCols || 1)) : 1
                  const rowMinHeight = Math.max(DAY_ROW_SLOT_HEIGHT, totalCols * DAY_ROW_SLOT_HEIGHT)
                  const slotHeight = rowMinHeight / totalCols

                  return (
                    <tr key={row.key}>
                      <th className={styles.transposedNameCell}>
                        <Text fw="500">{row.label}</Text>
                      </th>
                      <td
                        colSpan={hours.length}
                        className={styles.transposedRowCells}
                        style={{ height: rowMinHeight, position: "relative" }}
                      >
                        {rowIdx === 0 && showCurrentTimeLine && currentTimePercent !== null && (
                          <div
                            style={{
                              position: "absolute",
                              left: `${currentTimePercent}%`,
                              transform: "translateX(-50%)",
                              top: -12,
                              zIndex: 100
                            }}
                          >
                            <span className={styles.transposedTimeLineDot} />
                          </div>
                        )}
                        {hours.map((h, hIdx) => (
                          <div
                            key={h}
                            className={styles.transposedCell}
                            style={{ left: `${(hIdx / hours.length) * 100}%`, width: `${100 / hours.length}%` }}
                          />
                        ))}
                        {showCurrentTimeLine && currentTimePercent !== null && (
                          <div className={styles.transposedTimeLine} style={{ left: `${currentTimePercent}%` }} />
                        )}
                        {eventsWithCols.map((ev) => {
                          const startMin = Math.max(0, (ev.startMoment.hour() - startHour) * 60 + ev.startMoment.minute())
                          const endMin = Math.min(totalMins, (ev.endMoment.hour() - startHour) * 60 + ev.endMoment.minute())
                          const leftPct = (startMin / totalMins) * 100
                          const widthPct = Math.max(0, ((endMin - startMin) / totalMins) * 100)
                          const top = (ev.colIndex || 0) * slotHeight + 2
                          const height = slotHeight - 4
                          const conflict =
                            timeOffForDay?.allDay ||
                            (timeOffForDay?.ranges || []).some((r) => overlaps(ev.startMoment, ev.endMoment, r.start, r.end))
                          return (
                            <div
                              key={ev.id ?? ev.title + ev.start}
                              className={styles.transposedEvent}
                              style={{ left: `${leftPct}%`, width: `${widthPct}%`, top, height }}
                            >
                              <EventBlock
                                event={ev}
                                eventColor={getEventColor(ev.payment_status, ev.booking_status)}
                                eventBorderColor={getEventBorderColor(ev.payment_status)}
                                conflict={conflict}
                                isBlackoutConflict={marksConflicts && Boolean(getBookingConflict(ev))}
                                setIsBookingModalOpen={setIsBookingModalOpen}
                                setSelectedBookingId={setSelectedBookingId}
                                fullHeight
                                hideTime
                              />
                            </div>
                          )
                        })}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </Flex>
        {isJournalModalOpen && (
          <Journal
            open={isJournalModalOpen}
            setOpen={setIsJournalModalOpen}
            onCancel={() => setIsJournalModalOpen(false)}
            selectedDate={date}
            startDate={date}
            endDate={date}
          />
        )}
        {isBookingModalOpen && (
          <ReservationDetailModal
            open={isBookingModalOpen}
            bookingUuid={selectedBookingId}
            onClose={handleBookingCancelSuccess}
          />
        )}
      </>
    )
  }

  // ── Standard layout: time on left, resources as columns ──
  const columnCount = resources.length || 1
  // A multi-day trip has no meaningful position in a single day's hours, so it goes to the
  // strip above the grid and the columns keep only the day's own bookings.
  const multiDayEvents = dayEvents.filter((ev) => ev.multi_day)
  const singleDayEvents = dayEvents.filter((ev) => !ev.multi_day)

  return (
    <>
      <Flex vertical flex={1} style={{ minHeight: 0, overflow: "clip" }}>
        <Flex gap={8} align="center" justify="flex-end" className={styles.dayJournalRow}>
          <Text>{t("calendar.journal_label", { defaultValue: "Journal" })}</Text>
          {hasJournalPermission && (
            <IconWithBg
              icon={getJournalIcon(journalNoteDates, currentDay)}
              onClick={active ? () => setIsJournalModalOpen(true) : undefined}
              disabled={!active}
            />
          )}
        </Flex>
        {blackoutBanner}
        <MultiDayStrip
          dayKey={currentDayKey}
          bookings={multiDayEvents}
          getBookingConflict={marksConflicts ? getBookingConflict : null}
          setIsBookingModalOpen={setIsBookingModalOpen}
          setSelectedBookingId={setSelectedBookingId}
        />
        <div key={`grid-${columnCount}`} className={styles.timeGridRoot}>
          <div className={styles.timeGridHeaderRow} style={{ gridTemplateColumns, minWidth: gridMinWidth }}>
            <div className={styles.timeLabelHeader} />
            <div className={styles.timeDividerHeader} />
            {resources.map((resource) => {
              const label = getResourceLabel(resource)
              const key = typeof resource === "string" ? resource : (resource?.id ?? label)
              return (
                <div key={key} className={styles.resourceHeaderCell}>
                  <Text fw="500">{label}</Text>
                </div>
              )
            })}
          </div>
          <div
            className={styles.timeGridTable}
            style={{
              display: "grid",
              gridTemplateRows: `repeat(${hours.length - 1}, ${HOUR_HEIGHT}px) minmax(${HOUR_HEIGHT}px, 1fr)`,
              gridTemplateColumns,
              position: "relative",
              minWidth: gridMinWidth,
              height: "100%"
            }}
          >
            {showCurrentTimeLine && currentTimeOffset !== null && (
              <div
                className={styles.currentTimeLine}
                style={{ top: currentTimeOffset, gridColumn: "1 / -1", position: "absolute", zIndex: 6 }}
              >
                <span className={styles.currentTimeDot} />
              </div>
            )}
            {hours.map((hour, hIdx) => {
              const now = moment()
              const dayMoment = moment(date)
              const isPastHour = dayMoment.isBefore(now, "day") || (dayMoment.isSame(now, "day") && Number(hour) < now.hour())
              return (
                <div
                  key={`label-${hour}`}
                  className={`${styles.timeLabel} ${isPastHour ? styles.pastCalendarCell : ""}`}
                  style={{ gridRow: hIdx + 1, gridColumn: 1 }}
                >
                  <Text fw="500">{moment(hour, "H").format("h A")}</Text>
                </div>
              )
            })}
            <div style={{ gridRow: `1 / span ${hours.length}`, gridColumn: 2, background: "var(--color-surface3)", width: 1 }} />
            {resources.map((resource, resIdx) => {
              const resourceId = typeof resource === "string" ? null : resource?.id
              const resourceKey = typeof resource === "string" ? resource : (resource?.id ?? getResourceLabel(resource))
              const resEvents = getResourceEvents(singleDayEvents, resource, isEquipmentTab, isCrewTab, resourceId)
              const eventsWithCols = computeEventColumns(resEvents.filter((ev) => ev.startMoment?.isValid() && ev.endMoment?.isValid()))
              return (
                <div
                  key={resourceKey}
                  className={styles.dayColumn}
                  style={{ gridRow: `1 / span ${hours.length}`, gridColumn: resIdx + 3, minHeight: totalHeight, position: "relative" }}
                >
                  {hours.map((hour, hIdx) => {
                    const isPastHour =
                      moment(date).isBefore(moment(), "day") || (moment(date).isSame(moment(), "day") && Number(hour) < moment().hour())
                    return (
                      <div
                        key={`divider-${hour}`}
                        className={styles.hourDivider}
                        style={{ top: hIdx * HOUR_HEIGHT, ...(isPastHour ? { background: "var(--color-surface2)" } : {}) }}
                      />
                    )
                  })}
                  {timeOffForDay?.allDay && <div className={`${styles.timeOffOverlay} ${styles.timeOffOverlayFull}`} />}
                  {!timeOffForDay?.allDay &&
                    (timeOffForDay?.ranges || []).map((toff) => {
                      const topPx = Math.max(0, (toff.start.hour() - startHour) * 60 + toff.start.minute()) * (HOUR_HEIGHT / 60)
                      const botPx = Math.max(0, (toff.end.hour() - startHour) * 60 + toff.end.minute()) * (HOUR_HEIGHT / 60)
                      return (
                        <div
                          key={`${currentDay}-${resourceKey}-${toff.start.format("HH:mm")}`}
                          className={styles.timeOffOverlay}
                          style={{ top: topPx, height: Math.max(0, botPx - topPx) }}
                        />
                      )
                    })}
                  {eventsWithCols.map((ev) => {
                    const pos = getEventPosition(ev, startHour, endHour)
                    if (!pos) return null
                    const colW = 100 / ev.totalCols
                    const conflict =
                      timeOffForDay?.allDay ||
                      (timeOffForDay?.ranges || []).some((r) => overlaps(ev.startMoment, ev.endMoment, r.start, r.end))
                    return (
                      <div
                        key={ev.id ?? ev.title + ev.start}
                        className={styles.eventBlockPositioned}
                        style={{ top: pos.top, height: pos.height, left: `${ev.colIndex * colW}%`, width: `${colW}%` }}
                      >
                        <EventBlock
                          event={ev}
                          eventColor={ev.color}
                          eventBorderColor={getEventBorderColor()}
                          conflict={conflict}
                          isBlackoutConflict={marksConflicts && Boolean(getBookingConflict(ev))}
                          setIsBookingModalOpen={setIsBookingModalOpen}
                          setSelectedBookingId={setSelectedBookingId}
                          layout="title-first"
                          fullHeight
                        />
                      </div>
                    )
                  })}
                </div>
              )
            })}
          </div>
        </div>
      </Flex>
      {isJournalModalOpen && (
        <Journal
          open={isJournalModalOpen}
          setOpen={setIsJournalModalOpen}
          onCancel={() => setIsJournalModalOpen(false)}
          selectedDate={date}
          startDate={date}
          endDate={date}
        />
      )}
      {isBookingModalOpen && (
        <ReservationDetailModal
          open={isBookingModalOpen}
          bookingUuid={selectedBookingId}
          onClose={handleBookingCancelSuccess}
        />
      )}
    </>
  )
}

DayGrid.propTypes = {
  date: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  hours: PropTypes.array.isRequired,
  events: PropTypes.array.isRequired,
  resources: PropTypes.array,
  activeTab: PropTypes.string,
  profileIds: PropTypes.array,
  boatIds: PropTypes.array,
  startHour: PropTypes.number,
  endHour: PropTypes.number,
  showTimeRangeSettings: PropTypes.bool,
  onStartHourChange: PropTypes.func,
  onEndHourChange: PropTypes.func,
  onResetHours: PropTypes.func,
  onOpenDayPanel: PropTypes.func
}

export default DayGrid
